import React, { useEffect, useState } from "react";
import { ResponsivePie } from "@nivo/pie";
import { fetchPieceReferrals } from "../services/api";
import Spinner from "./Spinner";

const CompEntriesbyRefChart = ({ startDate, promptId }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAndTransformData = async () => {
      setIsLoading(true);
      try {
        const rawData = await fetchPieceReferrals(startDate, promptId);

        // Compute start and end based on startDate to match Looker Studio
        // start_date will always be the first day of a month
        // start should be last 2 days of the previous month at 08:00:00
        // end should be the last day of the month at 09:00:00 (inclusive of the full minute)
        const startOfMonth = new Date(`${startDate}T00:00:00`);
        startOfMonth.setDate(1); // Ensure we're at the first day of the month
        const start = new Date(startOfMonth);
        start.setDate(start.getDate() - 1); // Go back 2 days from first of month
        start.setHours(8, 0, 0, 0);

        const end = new Date(startOfMonth);
        end.setMonth(end.getMonth() + 1);
        end.setDate(0); // last day of the month
        end.setHours(9, 0, 0, 999); // Include all of 9:00 AM (up to 9:00:00.999)

        // Filter based on submitted_at not null, sentence exists, and date range
        const filtered = rawData.filter(entry => {
          // Exclude null, "None", "NaT" for submitted_at to match Looker Studio's "exclude submitted_at = is null"
          const hasSubmission = entry.submitted_at && 
                               entry.submitted_at !== "NaT" && 
                               entry.submitted_at !== "None" &&
                               entry.submitted_at.trim().toLowerCase() !== "none";
          // Filter out null, undefined, empty string, whitespace-only, or "None" sentences
          const hasSentence = entry.sentence && 
                             entry.sentence !== null && 
                             entry.sentence !== undefined &&
                             typeof entry.sentence === 'string' &&
                             entry.sentence.trim() !== "" &&
                             entry.sentence.trim().toLowerCase() !== "none";
          const createdAt = new Date(entry.created_at);
          const inDateRange = createdAt >= start && createdAt <= end;
          return hasSubmission && hasSentence && inDateRange;
        });

        // Group by sentence and count distinct wtw_profile_ids
        const usersBySentence = filtered.reduce((acc, entry) => {
          const label = entry.sentence.trim();
          if (!acc[label]) {
            acc[label] = new Set();
          }
          acc[label].add(entry.wtw_profile_id);
          return acc;
        }, {});

        // Convert Set sizes to counts
        const counts = Object.entries(usersBySentence).reduce((acc, [label, userSet]) => {
          acc[label] = userSet.size;
          return acc;
        }, {});

        // Format for Nivo
        const formattedData = Object.entries(counts).map(([label, count]) => ({
          id: label,
          label,
          value: count
        }));

        setData(formattedData);
      } catch (error) {
        console.error("Error loading referral data", error);
      } finally {
        setIsLoading(false);
      }
    };

    // reset for immediate spinner on change
    setData([]);
    fetchAndTransformData();
  }, [startDate, promptId]);

  return (
    <div style={{ height: "400px", maxWidth: "700px" }}>
      {isLoading ? (
        <Spinner label="Loading referral chart…" />
      ) : data.length > 0 ? (
        <ResponsivePie
          data={data}
          margin={{ top: 40, right: 160, bottom: 40, left: 40 }}
          innerRadius={0.4}
          padAngle={1}
          cornerRadius={3}
          colors={{ scheme: "category10" }}
          borderWidth={1}
          borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
          arcLabelsSkipAngle={10}
          arcLabelsTextColor={{ from: "color", modifiers: [["darker", 2]] }}
          arcLinkLabelsSkipAngle={10}
          arcLinkLabelsTextColor="#333333"
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: "color" }}
          // Show percentage labels on slices
          arcLabel={(d) => {
            const total = data.reduce((sum, item) => sum + item.value, 0);
            const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : "0.0";
            return `${pct}%`;
          }}
          // Custom tooltip: Label: count (percent)
          tooltip={({ datum }) => {
            const total = data.reduce((sum, item) => sum + item.value, 0);
            const pct = total > 0 ? ((datum.value / total) * 100).toFixed(1) : "0.0";
            return (
              <div style={{
                background: "white",
                padding: "6px 9px",
                border: "1px solid #ddd",
                borderRadius: 4,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                color: "#333"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 12, height: 12, background: datum.color, display: "inline-block", borderRadius: 2 }} />
                  <span style={{ fontWeight: 600 }}>{datum.id}</span>
                </div>
                <div style={{ marginTop: 4 }}>
                  <span style={{ fontWeight: 600 }}>{datum.value}</span>
                  <span>{` (${pct}%)`}</span>
                </div>
              </div>
            );
          }}
          legends={[
            {
              anchor: "right",
              direction: "column",
              translateX: 120,
              itemWidth: 100,
              itemHeight: 20,
              itemsSpacing: 4,
              symbolSize: 12,
              symbolShape: "circle"
            }
          ]}
        />
      ) : (
        <p>No data available.</p>
      )}
    </div>
  );
};

export default CompEntriesbyRefChart;
